// The MIT License (MIT)

// Copyright (c) 2017 Zalando SE

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
import { select } from 'd3-selection';

import { TechnologiesRadar } from 'types/radar.types';

import config from './radarConfig';
import { addRadiants, addRadarSweepGradient, addFocusQuarterGradient } from './radarFilters';
import { translate } from './radarUtils';
import { Callbacks } from './types';
import drawBlips from './utils/drawBlips';
import drawBubble from './utils/drawBubble';
import drawEnterAnimation from './utils/drawEnterAnimation';
import drawGridLines from './utils/drawGridLines';
import drawLegend from './utils/drawLegend';
import drawQuarters from './utils/drawQuarters';
import drawRings from './utils/drawRings';
import drawSweepAnimation from './utils/drawSweepAnimation';
import focusQuarter from './utils/focusQuarter';
import hideAura from './utils/hideAura';
import showBlipAura from './utils/showBlipAura';
import toggleQuarter from './utils/toggleQuarter';
import triggerEvent from './utils/triggerEvent';

const radarVisualization = ({
  technologies,
  callbacks,
}: {
  technologies: TechnologiesRadar | null;
  callbacks?: Callbacks;
}) => {
  const svg = select(`svg#${config.svg_id}`)
    .style('background-color', config.colors.background)
    .style('z-index', 3)
    .attr('viewBox', `0 0 ${config.width} ${config.height}`)
    .attr('preserveAspectRatio', 'xMidYMid meet');

  if (select('.container').size()) {
    select('.container').remove();
  }

  const radar = svg
    .append('g')
    .attr('class', 'container')
    .attr('transform', translate(config.width / 2, config.height / 2));

  const defs = svg.append('defs');

  addRadiants(defs);
  addFocusQuarterGradient(defs);
  addRadarSweepGradient(defs);

  radar.append('g').attr('class', 'enterAnimation');
  radar.append('g').attr('class', 'rings');
  radar.append('g').attr('class', 'grid');
  radar.append('g').attr('class', 'quarters');
  radar.append('g').attr('class', 'legend');
  radar.append('g').attr('class', 'rink');
  radar.append('g').attr('class', 'restBlips');

  drawEnterAnimation();
  drawRings();
  drawQuarters(callbacks);
  drawGridLines();
  drawLegend(callbacks);
  drawBubble();
  drawBlips(technologies, callbacks);
  drawSweepAnimation();
};

export { radarVisualization, triggerEvent, toggleQuarter, focusQuarter, hideAura, showBlipAura };
